import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import FileSaver from 'file-saver';
import { utils } from 'xlsx';

import '../../Assets/Pages/campagneSdr.css';

import { fetchApi } from '../../Network/NetworkService';
import { notif } from '../Ui/Notification';
import { exportAsExcel } from '../../Services/exportAsExcel';

// Mirrors the buckets in server/Controllers/Utils/RelanceTracking.js. They
// partition a run: every recipient is in exactly one, so they sum to the total.
const BUCKETS = [
    { key: 'silent', label: 'Aucune réaction' },
    { key: 'reacted', label: 'A réagi' },
    { key: 'converted', label: 'Converti' }
];

// Codes from WhatsappDrain.recipientBlock, plus 'reachable'.
const WHATSAPP_LABELS = {
    reachable: { label: 'Joignable', color: '#5cb85c' },
    optedOut: { label: 'STOP', color: '#d9534f' },
    noConsent: { label: 'Sans consentement', color: '#999' },
    invalidNumber: { label: 'Numéro invalide', color: '#999' },
    deleted: { label: 'Compte supprimé', color: '#999' }
};

// CLAUDE.md, WhatsApp > Status: resume with 50-100 recipients and re-check the
// quality rating before scaling. A "silent" list is the coldest audience there is.
//
// Advisory only, and deliberately: the cap is ENFORCED in enqueueCampaign and
// chosen on the WhatsApp tab, where the sender, the template and the number's
// live quality rating are all visible. Truncating the handoff here would hide
// that decision in the wrong screen, and would miss the other way a bulk run is
// started — the WhatsApp tab's own doctor and period cohort. This number exists
// to warn, not to cut.
//
// It is therefore NOT quoted as the limit in the copy below. The real cap comes
// from WHATSAPP_CAMPAIGN_LIMIT and is served as `limitDefault` by the preview
// route; stating a hardcoded number here as server behaviour would simply be
// false under any other configuration, and would make this a second source of
// truth for a value the server already publishes.
const WHATSAPP_SAFE_BATCH = 100;

// A run still `running` after this long lost its SSE connection mid-send.
const STALE_RUN_MS = 60 * 60 * 1000;

const PAGE_SIZE = 20;

const pct = (n, total) => (total ? Math.round((n / total) * 100) + '%' : '-');

const bucketLabel = (key) => BUCKETS.find((b) => b.key === key).label;

const whatsappLabel = (r) => (WHATSAPP_LABELS[r.whatsapp] || { label: '-' }).label;

// CSV opened directly in Excel evaluates a cell starting with one of these as a
// formula, and patient names are patient-controlled. Phone numbers get the same
// prefix so Excel keeps them as text instead of dropping the leading 0 / '+'.
const csvSafe = (v, forceText) => {
    const s = v === null || v === undefined ? '' : String(v);
    if (!s) return s;
    return forceText || /^[=+\-@\t\r]/.test(s) ? "'" + s : s;
};

const runStatus = (run) => {
    if (run.legacy) return { label: 'Reconstituée', title: "Envoi antérieur au suivi : destinataires retrouvés d'après les messages envoyés, approximatif" };
    if (run.status === 'failed') return { label: `Échec (${run.sentCount}/${run.recipientCount})` };
    if (run.status === 'running') {
        return Date.now() - new Date(run.sentAt).getTime() > STALE_RUN_MS
            ? { label: `Interrompue (${run.sentCount}/${run.recipientCount})` }
            : { label: `En cours (${run.sentCount}/${run.recipientCount})` };
    }
    return null;
};

const Badge = ({ color, children, title }) => (
    <span title={title} style={{ background: color, color: '#fff', borderRadius: 10, padding: '2px 8px', fontSize: 12, whiteSpace: 'nowrap' }}>
        {children}
    </span>
);

const RelanceSuivi = (props) => {
    const [runs, setRuns] = useState([]);
    const [runsLoading, setRunsLoading] = useState(false);
    const [selected, setSelected] = useState(null);
    const [bucket, setBucket] = useState('silent');
    // Every recipient of the selected run, fetched once; buckets filter it here.
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [page, setPage] = useState(1);
    // Only the latest detail request may land: a slower earlier one would
    // otherwise replace the table under a header that names another run.
    const detailSeq = useRef(0);

    const loadRuns = () => {
        setRunsLoading(true);
        fetchApi('/campagne-sdr/relance/runs?limit=30')
            .then((res) => {
                setRuns(res.runs || []);
                setRunsLoading(false);
            })
            .catch(() => {
                notif.addNotification('Erreur', 'Erreur de récupération des relances', 'warning');
                setRunsLoading(false);
            });
    };

    const loadDetail = (run) => {
        const seq = ++detailSeq.current;
        setDetail(null);
        setDetailLoading(true);
        fetchApi(`/campagne-sdr/relance/runs/${encodeURIComponent(run.key)}/recipients`)
            .then((res) => {
                if (seq !== detailSeq.current) return;
                setDetail({ key: run.key, ...res });
                setDetailLoading(false);
            })
            .catch(() => {
                if (seq !== detailSeq.current) return;
                notif.addNotification('Erreur', 'Erreur de récupération des destinataires', 'warning');
                setDetailLoading(false);
            });
    };

    useEffect(() => { loadRuns(); }, []);

    const openRun = (run, b) => {
        if (selected && selected.key === run.key) {
            if (bucket === b) {
                detailSeq.current++;
                setSelected(null);
                setDetail(null);
                setDetailLoading(false);
                return;
            }
            setBucket(b);
            setPage(1);
            return;
        }
        setSelected(run);
        setBucket(b);
        setPage(1);
        loadDetail(run);
    };

    // Rows of the bucket on screen, and only when they belong to the selected run.
    const rows = detail && selected && detail.key === selected.key
        ? detail.recipients.filter((r) => r.bucket === bucket)
        : [];
    const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
    const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const reachable = rows.filter((r) => r.whatsapp === 'reachable').length;

    // Hands the silent recipients to the WhatsApp bulk tab of Campagne SDR.
    // Only ids travel: the consent gate, opt-out suppression and number
    // normalisation all stay in enqueueCampaign, the one place that decides.
    const targetOnWhatsapp = () => {
        if (!selected || !rows.length || bucket !== 'silent') return;
        props.history.push('/campagne-sdr', {
            waTarget: {
                userIds: rows.filter((r) => !r.deleted).map((r) => r.userId),
                label: `Relance ${selected.doctorName} du ${moment(selected.sentAt).format('DD/MM/YYYY')} - sans réaction`,
                source: `${selected.doctorName}, ${moment(selected.sentAt).format('DD/MM/YYYY HH:mm')}`,
                reachable,
                approximate: !!detail.legacy
            }
        });
    };

    // Same rows for both formats: the whole bucket, not only the visible page.
    const exportRows = (forCsv) => rows.map((r) => ({
        'Prénom': forCsv ? csvSafe(r.FName) : r.FName,
        'Nom': forCsv ? csvSafe(r.LName) : r.LName,
        'Email': forCsv ? csvSafe(r.email) : r.email,
        'Téléphone': forCsv ? csvSafe(r.phone, true) : r.phone,
        'Téléphone (E.164)': r.e164 ? (forCsv ? csvSafe('+' + r.e164, true) : '+' + r.e164) : '',
        'Statut': bucketLabel(r.bucket),
        'Envoyé le': moment(r.sentAt).format('DD/MM/YYYY HH:mm'),
        'Message lu': r.read ? 'Oui' : 'Non',
        'Lu le': r.readAt ? moment(r.readAt).format('DD/MM/YYYY') : '',
        'Consultations depuis': r.convertedRequests,
        'Autres demandes depuis': r.otherRequests,
        'WhatsApp': whatsappLabel(r)
    }));

    const exportFileName = () => [
        'relance',
        (selected.doctorName || '').replace(/\s+/g, '_'),
        moment(selected.sentAt).format('YYYY-MM-DD'),
        bucket
    ].join('_');

    // xlsx cells are typed: strings stay strings, so no formula and no lost 0.
    const exportExcel = () => {
        exportAsExcel(exportRows(false), exportFileName(), [
            { wch: 15 }, { wch: 18 }, { wch: 30 }, { wch: 15 }, { wch: 16 }, { wch: 16 },
            { wch: 16 }, { wch: 10 }, { wch: 11 }, { wch: 12 }, { wch: 12 }, { wch: 18 }
        ]);
    };

    // Semicolons and a BOM: what French Excel expects when opening a .csv
    // directly, otherwise everything lands in one column with broken accents.
    const exportCsv = () => {
        const csv = utils.sheet_to_csv(utils.json_to_sheet(exportRows(true)), { FS: ';' });
        FileSaver.saveAs(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }), exportFileName() + '.csv');
    };

    return (
        <div className="sdr-container">
            <h2 className="sdr-title">Suivi des relances</h2>

            <div className="sdr-card">
                <p className="sdr-hint">
                    Les emails de relance n'ont pas de suivi d'ouverture. On mesure donc des réactions
                    réelles, dans les 30 jours suivant l'email de chaque patient :{' '}
                    <strong>Converti</strong> = nouvelle consultation (mêmes statuts que la page Cohortes),{' '}
                    <strong>A réagi</strong> = a lu le message du médecin dans l'application ou a fait une
                    autre demande (incomplète, annulée, remboursée...), <strong>Aucune réaction</strong> = ni
                    l'un ni l'autre : ce sont eux qu'il faut relancer sur un autre canal.
                </p>
                <p className="sdr-hint">
                    Les relances « reconstituées » sont antérieures au suivi : leurs destinataires sont retrouvés
                    d'après les messages envoyés, la liste est donc approximative. Vérifiez-la avant de
                    l'utiliser comme cible WhatsApp.
                </p>

                {runsLoading && <p>Chargement...</p>}
                {!runsLoading && !runs.length && (
                    <div className="sdr-empty">Aucune relance trouvée</div>
                )}

                {!!runs.length && (
                    <div className="sdr-table-wrap">
                        <table className="sdr-table">
                            <thead>
                                <tr>
                                    <th>Envoyée le</th>
                                    <th>Médecin</th>
                                    <th>Message</th>
                                    <th>Destinataires</th>
                                    {BUCKETS.map((b) => <th key={b.key}>{b.label}</th>)}
                                    <th>Sans réaction avec consentement WhatsApp</th>
                                </tr>
                            </thead>
                            <tbody>
                                {runs.map((run) => {
                                    const c = run.counts;
                                    const isOpen = selected && selected.key === run.key;
                                    const status = runStatus(run);
                                    return (
                                        <tr key={run.key} style={isOpen ? { background: '#f8f9fb' } : null}>
                                            <td>
                                                {moment(run.sentAt).format('DD/MM/YYYY HH:mm')}
                                                {status && (
                                                    <div><Badge color="#999" title={status.title}>{status.label}</Badge></div>
                                                )}
                                            </td>
                                            <td>
                                                {run.doctorName}
                                                {run.cohortStart && run.cohortEnd && (
                                                    <div className="sdr-hint" style={{ margin: 0 }}>
                                                        Inscrits du {moment(run.cohortStart).format('DD/MM/YY')} au {moment(run.cohortEnd).format('DD/MM/YY')}
                                                    </div>
                                                )}
                                            </td>
                                            <td style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={run.subject || ''}>
                                                {run.subject || <em>message introuvable</em>}
                                            </td>
                                            <td>{c.total}</td>
                                            {BUCKETS.map((b) => (
                                                <td key={b.key}>
                                                    <button
                                                        className={'sdr-btn ' + (isOpen && bucket === b.key ? 'sdr-btn-primary' : 'sdr-btn-secondary')}
                                                        disabled={!c[b.key]}
                                                        onClick={() => openRun(run, b.key)}
                                                    >
                                                        {c[b.key]} ({pct(c[b.key], c.total)})
                                                    </button>
                                                </td>
                                            ))}
                                            <td>{c.silentWithConsent}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {selected && (
                <div className="sdr-card">
                    <h3 className="sdr-card-title">
                        {bucketLabel(bucket)} : relance {selected.doctorName} du{' '}
                        {moment(selected.sentAt).format('DD/MM/YYYY')}
                    </h3>
                    {selected.subject && <p className="sdr-card-desc">« {selected.subject} »</p>}

                    {detailLoading && <p>Chargement...</p>}

                    {!detailLoading && detail && detail.key === selected.key && (
                        <>
                            {bucket === 'silent' && detail.legacy && (
                                <p className="sdr-hint" style={{ color: '#f0ad4e' }}>
                                    ⚠️ Liste reconstituée : cette relance date d'avant le suivi, ses destinataires sont
                                    retrouvés d'après les messages envoyés et peuvent être approximatifs. Vérifiez-la
                                    avant d'envoyer.
                                </p>
                            )}
                            {bucket === 'silent' && (
                                <div className="sdr-stats-bar">
                                    <span>
                                        Sans réaction : <strong>{rows.length}</strong>, dont joignables sur WhatsApp :{' '}
                                        <strong>{reachable}</strong>
                                    </span>
                                    <button
                                        className="sdr-btn sdr-btn-primary"
                                        disabled={!rows.length}
                                        onClick={targetOnWhatsapp}
                                    >
                                        <i className="zmdi zmdi-whatsapp" /> Cibler sur WhatsApp
                                    </button>
                                </div>
                            )}
                            {bucket === 'silent' && reachable > WHATSAPP_SAFE_BATCH && (
                                <p className="sdr-hint" style={{ color: '#d9534f' }}>
                                    ⚠️ {reachable} destinataires joignables. Ceux qui n'ont pas réagi à l'email sont
                                    l'audience la plus froide, la plus susceptible de bloquer ou signaler, et ce sont ces
                                    signaux qui font baisser la note de qualité du numéro. Une limite est appliquée
                                    à l'envoi : l'onglet WhatsApp affiche sa valeur, le nombre réel de messages et
                                    les destinataires non retenus avant que vous ne lanciez quoi que ce soit.
                                </p>
                            )}
                            {bucket === 'silent' && reachable < rows.length && (
                                <p className="sdr-hint">
                                    Les personnes sans consentement WhatsApp, sans numéro valide ou ayant répondu
                                    STOP seront ignorées par la campagne. Elles apparaîtront comme « ignorées » dans son
                                    rapport, et aucun message ne leur sera envoyé.
                                </p>
                            )}

                            <div className="sdr-inline" style={{ marginBottom: 12 }}>
                                <button className="sdr-btn sdr-btn-secondary" disabled={!rows.length} onClick={exportExcel}>
                                    <i className="zmdi zmdi-download" /> Excel ({rows.length})
                                </button>
                                <button className="sdr-btn sdr-btn-secondary" disabled={!rows.length} onClick={exportCsv}>
                                    <i className="zmdi zmdi-download" /> CSV ({rows.length})
                                </button>
                            </div>

                            <div className="sdr-table-wrap">
                                <table className="sdr-table">
                                    <thead>
                                        <tr>
                                            <th>Patient</th>
                                            <th>Email</th>
                                            <th>Téléphone</th>
                                            <th>Message lu</th>
                                            <th>Demandes depuis</th>
                                            <th>WhatsApp</th>
                                            <th />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pageRows.map((r) => {
                                            const wa = WHATSAPP_LABELS[r.whatsapp];
                                            return (
                                                <tr key={r.userId}>
                                                    <td>{r.FName} {r.LName}</td>
                                                    <td>{r.email}</td>
                                                    <td>{r.phone || '-'}</td>
                                                    <td>{r.read ? (r.readAt ? moment(r.readAt).format('DD/MM/YYYY') : 'Oui') : 'Non'}</td>
                                                    <td>
                                                        {r.convertedRequests > 0 && <span>{r.convertedRequests} consultation(s) </span>}
                                                        {r.otherRequests > 0 && <span>{r.otherRequests} autre(s)</span>}
                                                        {!r.convertedRequests && !r.otherRequests && '-'}
                                                    </td>
                                                    <td>{wa && <Badge color={wa.color}>{wa.label}</Badge>}</td>
                                                    <td>
                                                        <button
                                                            className="sdr-btn sdr-btn-secondary"
                                                            onClick={() => props.history.push('/users/detail/' + r.userId)}
                                                        >
                                                            Détails
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {pageCount > 1 && (
                                <div className="sdr-inline" style={{ marginTop: 12, alignItems: 'center' }}>
                                    <button className="sdr-btn sdr-btn-secondary" disabled={page < 2} onClick={() => setPage(page - 1)}>
                                        Précédent
                                    </button>
                                    <span>Page {page} / {pageCount}</span>
                                    <button className="sdr-btn sdr-btn-secondary" disabled={page >= pageCount} onClick={() => setPage(page + 1)}>
                                        Suivant
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default RelanceSuivi;
